import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { workflowDetailQueryOptions } from '@/features/order-confirmation/workflowListQueries'
import { ArrowLeft, ExternalLink } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Container, ContainerContent } from '@/components/ui/Container'
import { getInboxEmail } from '@/services/api'
import type { InboxAttachment, InboxOC } from '@/types/inbox'
import { EmailBody } from '@/components/EmailBody'
import { AttachmentSection } from '../components/OcSource'
import { inboxAttachmentToReference } from '@/utils/attachmentStorage'
import { cn } from '@/lib/utils'
import { EmailMetaPanel } from '@/features/order-confirmation/inbox/EmailMetaPanel'
import { OutlookFolderBadge } from '@/features/order-confirmation/inbox/OutlookFolderBadge'

function formatDateTime(iso: string): string {
  try {
    const d = new Date(iso)
    return d.toLocaleString('sv-SE', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    })
  } catch { return iso }
}

function OcLink({ oc, workflowId }: { oc: InboxOC; workflowId: string }) {
  if (oc.oc_status !== 'open') return null

  if (oc.purchase_order_id) {
    return (
      <Button type="button" variant="outline" size="sm" className="w-full gap-1.5" asChild>
        <Link to={`/workflow/${workflowId}/purchase-order/${oc.purchase_order_id}`}>
          <ExternalLink className="h-3.5 w-3.5" />
          {oc.purchase_order_number ? `PO ${oc.purchase_order_number}` : 'Open purchase order'}
        </Link>
      </Button>
    )
  }
  return (
    <Button type="button" variant="outline" size="sm" className="w-full gap-1.5" asChild>
      <Link to={`/workflow/${workflowId}/unmatched-oc/${oc.oc_id}`}>
        <ExternalLink className="h-3.5 w-3.5" />
        Open order confirmation
      </Link>
    </Button>
  )
}

function AttachmentCard({ attachment, workflowId }: { attachment: InboxAttachment; workflowId: string }) {
  return (
    <Container className="min-w-0">
      <ContainerContent className="space-y-4 p-5">
        <AttachmentSection attachment={inboxAttachmentToReference(attachment)} prominent />
        {attachment.order_confirmations.map((oc) => (
          <OcLink key={oc.oc_id} oc={oc} workflowId={workflowId} />
        ))}
      </ContainerContent>
    </Container>
  )
}

export function EmailDetailPage() {
  const { workflowId, emailId } = useParams<{ workflowId: string; emailId: string }>()

  const { data, isLoading, isError } = useQuery({
    queryKey: ['inbox-email', workflowId, emailId],
    queryFn: () => getInboxEmail(workflowId!, emailId!),
    enabled: !!workflowId && !!emailId,
    ...workflowDetailQueryOptions,
  })

  if (isLoading) {
    return <div className="flex items-center justify-center py-16 text-sm text-gray-400">Loading…</div>
  }

  if (isError || !data) {
    return (
      <div className="max-w-lg space-y-4">
        <p className="text-sm text-gray-600">Could not load this email.</p>
        <Button type="button" variant="outline" size="sm" asChild>
          <Link to={`/workflow/${workflowId}/inbox`}>Back to inbox</Link>
        </Button>
      </div>
    )
  }

  return (
    <div className="container mx-auto space-y-6">
      {/* Back */}
      <Button type="button" variant="ghost" size="sm" className="-ml-2 gap-1 text-on-canvas-muted hover:bg-white/40 hover:text-on-canvas" asChild>
        <Link to={`/workflow/${workflowId}/inbox`}>
          <ArrowLeft className="h-4 w-4" />
          Inbox
        </Link>
      </Button>

      <div className={cn(
        'grid grid-cols-1 items-stretch gap-4',
        data.attachments.length > 0 && 'lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]',
      )}>
        {/* Email */}
        <Container className="min-w-0">
          <ContainerContent className="p-0">
            <div className="border-b border-gray-100 px-5 py-4">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 space-y-1">
                  <h1 className="text-base font-semibold text-gray-900">
                    {data.subject || '(no subject)'}
                  </h1>
                  <p className="text-sm text-gray-600">{data.sender_address}</p>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1">
                  <OutlookFolderBadge folder={data.outlook_folder} />
                  <p className="text-xs text-gray-400">{formatDateTime(data.received)}</p>
                </div>
              </div>
            </div>
            <div className="max-h-[min(70vh,720px)] overflow-y-auto px-5 py-4">
              {data.body
                ? <EmailBody html={data.body} plainTextClassName="text-sm text-gray-700" />
                : <p className="text-sm italic text-gray-400">(no body)</p>
              }
            </div>
            <div className="border-t border-gray-100 px-5 py-3">
              <EmailMetaPanel workflowId={workflowId!} emailId={emailId!} />
            </div>
          </ContainerContent>
        </Container>

        {/* Attachments — same 40% column weight as OC panel on purchase order detail */}
        {data.attachments.length > 0 && (
          <div className="min-w-0 space-y-4">
            {data.attachments.map((a) => (
              <AttachmentCard key={a.id} attachment={a} workflowId={workflowId!} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
